/**
 * Zentangle patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const zentanglePatternsPack: ToilePack = {
    slug: 'zentangle-patterns',
    category: 'coloring',
    name: 'Zentangle patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'owl', label: 'Owl', subject: 'a front-facing owl on a bare branch, its eyes, ear tufts, breast and fanned tail divided into pattern zones'},
        {key: 'feather', label: 'Feather', subject: 'a single long feather with a split vane, its barbs grouped into bands and the quill left open'},
        {key: 'heart', label: 'Heart mandala', subject: 'a symmetrical heart of concentric mandala rings, petals and scalloped arcs radiating from its centre'},
    ],
    styles: [
        {key: 'dense', label: 'Dense tangle', style: 'a dense classic tangle, every zone packed edge to edge with a different repeating motif — crosshatch, scales, spirals, brickwork, feather ribs', palette: 'Black ink only', framing: 'The shape centred in the square with a clean white margin.'},
        {key: 'bold', label: 'Bold outline', style: 'a bold-outline tangle, a thick contour around the shape and each zone, the motifs inside large and open with blank ground between marks', palette: 'Black ink only', framing: 'The design filling the full square to a narrow white border.'},
        {key: 'stipple', label: 'Stippled', style: 'a stippled tangle, the zones parted by fine contours and worked in dot stippling that builds depth from light to dark', palette: 'Black ink only', framing: 'The subject isolated at the centre, the paper around it left blank.'},
    ],
    directions: ['an elephant instead', 'as a bookmark strip', 'simpler, for a beginner'],
    seo: {
        title: 'Zentangle patterns: owl, feather, heart mandala',
        metaDescription: 'Nine zentangle pattern pages — owl, feather, heart mandala — dense, bold-outline or stippled, square pages for adults.',
        h1: 'Zentangle patterns: owl, feather and heart mandala in three densities',
        tagline: 'Nine zentangle pages, three shapes, three densities each — the pattern does the work, coloring becomes shading.',
        intro: 'The animal or shape divided into zones, each filled with a different repeating tangle: crosshatch, scales, spirals, brickwork. An owl, a feather, a heart mandala — dense and packed for a meditative fill, bold-outlined with open ground between marks, or stippled for a shaded look. Square pages, adult detail. Ask for a different shape or animal.',
        useCases: [
            'a mindful coloring session',
            'an adult coloring circle',
            'a stress-relief activity',
            'a framed square print',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn square (1024 by 1024) with a white margin, so it prints or frames clean at any size.'},
            {q: 'How dense are the patterns?', a: 'The dense style packs every zone edge to edge; the bold-outline style leaves open ground between marks; stippled builds shading from dots.'},
            {q: 'Can I get a different animal or shape?', a: 'Yes — that is what the direction field on the page is for. Type a different shape and it is drawn again at all three densities.'},
        ],
    },
}
